import { defineStore } from 'pinia'
import { Travel } from '../types/travelTypes'
import { formatTime } from '../utils/dateUtils'
import { formatShortNorwegianDate } from '../utils/dateFormatter'
import type { SelectedSeat } from '../types/seatTypes'
import type { Passenger } from '../types/passengerTypes'
import {
  NormalizedPrice,
  normalizePrice,
  normalizeTicketPrice,
  normalizeEquipmentPrice,
  normalizeSeatPrice,
} from '../utils/priceNormalization'

interface TravelData {
  departureTime: string
  arrivalTime: string
  travelDuration: string
}

interface PassengerCount {
  count: number
  price: NormalizedPrice | null
  name: string
  product_family: string
}

interface PassengerFare {
  passenger_id: string
  price?: NormalizedPrice | null
  original_price?: NormalizedPrice | null
  inventory_class?: string
  tariff_code?: string
}

interface Equipment {
  id?: string
  product_type: string
  product_code: string
  product_family?: string
  comfort_zone?: string
  name: string
  passenger_fares: Array<PassengerFare>
  tariff_code: string
  count: number
  price: NormalizedPrice | null
}

interface UIPrice {
  amount: number
  currency_code: string
}

interface UIPassengerFare {
  passenger_id: string
  price: UIPrice
  original_price: UIPrice
  inventory_class: string
  tariff_code: string
}

interface UIPassengerFare {
  passenger_id: string
  price: UIPrice
  original_price: UIPrice
  inventory_class: string
  tariff_code: string
}

interface Discount {
  name: string
  code: string
  amount: number
  percentage: number
  type: string
  voucher_id?: string
  provisional: boolean
  cancelled: boolean
}

interface Ticket {
  id?: string
  passenger_id: string
  passenger_type: string
  price?: NormalizedPrice | null
  total_price_to_be_paid?: NormalizedPrice | null
  original_price?: NormalizedPrice | null
  product_family?: string
  tariff_code?: string
  passenger_fares?: PassengerFare[]
  arrival_timestamp?: string
  departure_timestamp?: string
  ticket_name?: string
}

interface SelectedTickets {
  tickets: Ticket[]
}

interface Station {
  _u_i_c_station_code: string
  name: string
  short_code: string
  country_code: string
  connected_stations: any[]
  timezone: string
  meta_data: any[]
}

interface BookingSegment {
  id: string
  uuid: string
  departure_station: Station
  arrival_station: Station
  travel_date: string
  sequence_number: number
  has_new_main_products: boolean
  validity_service: string
  comfort_zone_code: string
  validity_start_date: string
  validity_end_date: string
  validity_intervals: Array<{
    start: string
    end: string
  }>
  validity_weekdays: string
  tariff_group_code: string
  booking_journey_segments: any[]
  required_products: Array<{
    id?: string
    name: string
    code: string
    tariff_code: string
    inventory_class: string
    passenger_id: string
    product_family_id: string
    item_id: string
    uuid: string
    bucket_code: string
    item_ref: number
    price: NormalizedPrice | null
    price_unconverted: NormalizedPrice | null
    original_price: NormalizedPrice | null
    original_price_unconverted: NormalizedPrice | null
    currency: string
    type: string
    ticket_number: string
    validity_count: number
    validations: any[]
    discounts: any[]
    seat_properties: any[]
    applied_seat_properties: any[]
    can_be_rebooked: boolean
    has_rebook_fee: boolean
    can_be_cancelled: boolean
    can_be_refunded: boolean
    can_be_upgraded: boolean
    has_refund_fee: boolean
    can_change_seat: boolean
    can_override_price: boolean
    can_change_name: boolean
    cancelled: boolean
    provisional: boolean
    meta_data: any[]
    after_sales_rules: any[]
  }>
  additional_products: any[]
  fees: any[]
  travel_info: any[]
  travel_info_extended: any[]
  service_type: {
    name: string
    code: string
    modality: string
  }
  client_ref: string
  is_international: boolean
  service_identifier: string
  tickets: Ticket[]
}

interface BookingData {
  booking_number?: string
  booking_id?: string
  revision_number?: number
  first_confirm_revision?: number
  sales_channel_code?: string
  total_price_to_be_paid?: NormalizedPrice | null
  total_price_unconverted?: NormalizedPrice | null
  discounts?: Discount[]
  vouchers?: Array<{
    id: string
    code: string
    alias: string
    type: string
    description: string
    amount: number
    currency: string
    payment_id: string
    active: boolean
  }>
  outbound_booking_tariff_segments?: BookingSegment[]
  inbound_booking_tariff_segments?: BookingSegment[]
}

interface TravelState {
  origin: string
  destination: string
  hasReturnTravel: boolean
  outboundDate: string
  returnDate: string
  outboundTravel: Travel | null
  inboundTravel: Travel | null
  outboundTravelData: TravelData
  inboundTravelData: TravelData
  outboundPassengerCounts: Record<string, PassengerCount>
  inboundPassengerCounts: Record<string, PassengerCount>
  outboundGroupedEquipment: Equipment[]
  inboundGroupedEquipment: Equipment[]
  outboundSelectedSeats: SelectedSeat[]
  inboundSelectedSeats: SelectedSeat[]
  outboundTotalSeatPrice: NormalizedPrice | null
  inboundTotalSeatPrice: NormalizedPrice | null
  outboundTotalEquipmentPrice: NormalizedPrice | null
  inboundTotalEquipmentPrice: NormalizedPrice | null
  outboundSelectedTickets: SelectedTickets
  inboundSelectedTickets: SelectedTickets
  discounts: Discount[]
  totalPriceToBePaid: NormalizedPrice | null
}

function toUIPassengerFare(fare: PassengerFare, currency = 'NOK'): UIPassengerFare {
  return {
    passenger_id: fare.passenger_id,
    price: {
      amount: fare.price?.amount || 0,
      currency_code: fare.price?.currency_code || currency,
    },
    original_price: {
      amount: fare.original_price?.amount || 0,
      currency_code: fare.original_price?.currency_code || currency,
    },
    inventory_class: fare.inventory_class || 'STANDARD',
    tariff_code: fare.tariff_code || '',
  }
}

function toUIEquipment(equipment: Equipment): any {
  return {
    ...equipment,
    passenger_fares: equipment.passenger_fares.map((fare) => toUIPassengerFare(fare)),
  }
}

function toUITicket(ticket: Ticket): any {
  return {
    ...ticket,
    passenger_fares: ticket.passenger_fares?.map((fare) => toUIPassengerFare(fare)),
  }
}

export const useMyTravelStore = defineStore('myTravel', {
  state: (): TravelState => ({
    origin: '',
    destination: '',
    hasReturnTravel: false,
    outboundDate: '',
    returnDate: '',
    outboundTravel: null,
    inboundTravel: null,
    outboundTravelData: {
      departureTime: '',
      arrivalTime: '',
      travelDuration: '',
    },
    inboundTravelData: {
      departureTime: '',
      arrivalTime: '',
      travelDuration: '',
    },
    outboundPassengerCounts: {},
    inboundPassengerCounts: {},
    outboundGroupedEquipment: [],
    inboundGroupedEquipment: [],
    outboundSelectedSeats: [],
    inboundSelectedSeats: [],
    outboundTotalSeatPrice: null,
    inboundTotalSeatPrice: null,
    outboundTotalEquipmentPrice: null,
    inboundTotalEquipmentPrice: null,
    outboundSelectedTickets: { tickets: [] },
    inboundSelectedTickets: { tickets: [] },
    discounts: [],
    totalPriceToBePaid: null,
  }),

  getters: {
    outboundTotalPrice(): NormalizedPrice {
      if (!this.outboundSelectedTickets?.tickets?.length) {
        return normalizePrice(0)
      }

      // Calculate ticket total
      const total = this.outboundSelectedTickets.tickets.reduce((acc, ticket) => {
        // First try to use total_price_to_be_paid
        if (ticket.total_price_to_be_paid) {
          return acc + (ticket.total_price_to_be_paid.amount || 0)
        }

        // Then try to use price
        if (ticket.price) {
          return acc + (ticket.price.amount || 0)
        }

        // Finally try passenger_fares
        const passengerFarePrice = ticket.passenger_fares?.[0]?.price
        if (passengerFarePrice) {
          return acc + (passengerFarePrice.amount || 0)
        }

        return acc
      }, 0)

      return normalizePrice(total)
    },

    inboundTotalPrice(): NormalizedPrice {
      if (!this.inboundSelectedTickets?.tickets?.length) {
        return normalizePrice(0)
      }

      // Calculate ticket total
      const total = this.inboundSelectedTickets.tickets.reduce((acc, ticket) => {
        // First try to use total_price_to_be_paid
        if (ticket.total_price_to_be_paid) {
          return acc + (ticket.total_price_to_be_paid.amount || 0)
        }

        // Then try to use price
        if (ticket.price) {
          return acc + (ticket.price.amount || 0)
        }

        // Finally try passenger_fares
        const passengerFarePrice = ticket.passenger_fares?.[0]?.price
        if (passengerFarePrice) {
          return acc + (passengerFarePrice.amount || 0)
        }

        return acc
      }, 0)

      return normalizePrice(total)
    },

    totalPrice(): NormalizedPrice {
      const outbound = this.outboundTotalPrice.amount || 0
      const inbound = this.inboundTotalPrice.amount || 0
      return normalizePrice(outbound + inbound)
    },
  },

  actions: {
    reset() {
      // Reset all state to initial values
      this.$reset()
      // Clear selected tickets specifically
      this.outboundSelectedTickets = { tickets: [] }
      this.inboundSelectedTickets = { tickets: [] }
      // Reset travel selections
      this.outboundTravel = null
      this.inboundTravel = null
      // Reset passenger counts
      this.outboundPassengerCounts = {}
      this.inboundPassengerCounts = {}
    },
    processTicketPrices(tickets: Ticket[]) {
      return tickets.map((ticket) => {
        const passengerFare = ticket.passenger_fares?.[0]

        // First try to use existing prices to maintain persistence
        const existingPrice = ticket.price || ticket.total_price_to_be_paid || ticket.original_price

        // Passenger prices are always in kroner from the API
        const passengerFarePrice = passengerFare?.price?.amount || 0
        const passengerFareOriginalPrice = passengerFare?.original_price?.amount || 0

        console.log('[processTicketPrices] Raw passenger price:', {
          existingPrice,
          passengerFarePrice,
          passengerFareOriginalPrice,
          ticketPrice: ticket.price,
          totalPriceToBePaid: ticket.total_price_to_be_paid,
          originalPrice: ticket.original_price,
          passengerFare,
        })

        const normalizedPrice = normalizePrice(
          existingPrice || passengerFarePrice || 0,
          'NOK',
          false,
        )
        console.log('[processTicketPrices] Normalized price:', normalizedPrice)

        return {
          ...ticket,
          // Preserve the original passenger_fares structure for API compatibility
          passenger_fares: ticket.passenger_fares,
          // Use existing price if available, fall back to passenger fare price (in kroner)
          price: normalizedPrice,
          total_price_to_be_paid: normalizePrice(
            existingPrice || passengerFarePrice || 0,
            'NOK',
            false,
          ),
          original_price: normalizePrice(
            existingPrice || passengerFareOriginalPrice || 0,
            'NOK',
            false,
          ),
        }
      })
    },
    saveStateToLocalStorage() {
      try {
        // Process outbound tickets to ensure prices are preserved
        const outboundTickets =
          this.outboundSelectedTickets?.tickets?.map((ticket: Ticket) => ({
            ...ticket,
            price: normalizePrice(ticket.price || 0),
            total_price_to_be_paid: normalizePrice(
              ticket.total_price_to_be_paid || ticket.price || 0,
            ),
            original_price: normalizePrice(ticket.original_price || ticket.price || 0),
          })) || []

        // Process inbound tickets to ensure prices are preserved
        const inboundTickets =
          this.inboundSelectedTickets?.tickets?.map((ticket: Ticket) => ({
            ...ticket,
            price: normalizePrice(ticket.price || 0),
            total_price_to_be_paid: normalizePrice(
              ticket.total_price_to_be_paid || ticket.price || 0,
            ),
            original_price: normalizePrice(ticket.original_price || ticket.price || 0),
          })) || []

        const stateToSave = {
          origin: this.origin,
          destination: this.destination,
          hasReturnTravel: this.hasReturnTravel,
          outboundDate: this.outboundDate,
          returnDate: this.returnDate,
          outboundTravel: this.outboundTravel,
          inboundTravel: this.inboundTravel,
          outboundTravelData: this.outboundTravelData,
          inboundTravelData: this.inboundTravelData,
          outboundPassengerCounts: this.outboundPassengerCounts,
          inboundPassengerCounts: this.inboundPassengerCounts,
          outboundGroupedEquipment: this.outboundGroupedEquipment,
          inboundGroupedEquipment: this.inboundGroupedEquipment,
          outboundSelectedSeats: this.outboundSelectedSeats,
          inboundSelectedSeats: this.inboundSelectedSeats,
          outboundTotalSeatPrice: this.outboundTotalSeatPrice,
          inboundTotalSeatPrice: this.inboundTotalSeatPrice,
          outboundTotalEquipmentPrice: this.outboundTotalEquipmentPrice,
          inboundTotalEquipmentPrice: this.inboundTotalEquipmentPrice,
          outboundSelectedTickets: { tickets: outboundTickets },
          inboundSelectedTickets: { tickets: inboundTickets },
          discounts: this.discounts,
          totalPriceToBePaid: this.totalPriceToBePaid,
        }
        localStorage.setItem('pendingMyTravelState', JSON.stringify(stateToSave))
      } catch (error) {
        console.error('Error saving MyTravel state:', error)
      }
    },

    restoreStateFromLocalStorage() {
      try {
        const savedState = localStorage.getItem('pendingMyTravelState')
        if (savedState) {
          const state = JSON.parse(savedState)

          // Process outbound tickets to ensure prices are numbers
          if (state.outboundSelectedTickets?.tickets) {
            state.outboundSelectedTickets.tickets = state.outboundSelectedTickets.tickets.map(
              (ticket: Ticket) => ({
                ...ticket,
                price: normalizePrice(ticket.price || 0),
                total_price_to_be_paid: normalizePrice(
                  ticket.total_price_to_be_paid || ticket.price || 0,
                ),
                original_price: normalizePrice(ticket.original_price || ticket.price || 0),
              }),
            )
          }

          // Process inbound tickets to ensure prices are numbers
          if (state.inboundSelectedTickets?.tickets) {
            state.inboundSelectedTickets.tickets = state.inboundSelectedTickets.tickets.map(
              (ticket: Ticket) => ({
                ...ticket,
                price: normalizePrice(ticket.price || 0),
                total_price_to_be_paid: normalizePrice(
                  ticket.total_price_to_be_paid || ticket.price || 0,
                ),
                original_price: normalizePrice(ticket.original_price || ticket.price || 0),
              }),
            )
          }

          Object.assign(this, state)
        }
      } catch (error) {
        console.error('Error restoring MyTravel state:', error)
      }
    },

    setOriginDestination(origin: string, destination: string) {
      this.origin = origin
      this.destination = destination
    },

    setHasReturnTravel(hasReturn: boolean) {
      this.hasReturnTravel = hasReturn
    },

    setDates(outbound: string, returnDate: string | null) {
      this.outboundDate = outbound ? formatShortNorwegianDate(outbound) : ''
      this.returnDate = returnDate ? formatShortNorwegianDate(returnDate) : ''
    },

    setOutboundTravel(travel: Travel | null) {
      const hasTransfers = Boolean(travel?.routes?.[0]?.legs?.length > 1)

      // Save current tickets, equipment, seats and prices before updating travel
      const currentTickets = this.outboundSelectedTickets?.tickets || []
      const currentEquipment = this.outboundGroupedEquipment || []
      const currentSeats = this.outboundSelectedSeats || []
      const currentSeatPrice = this.outboundTotalSeatPrice || 0

      // Check if the segments match for transfer journeys
      let sameSegments = true
      if (hasTransfers && travel?.routes?.[0]?.legs) {
        const currentSegments = this.outboundTravel?.routes?.[0]?.legs?.map((leg) => leg.id)
        const newSegments = travel.routes[0].legs.map((leg) => leg.id)
        sameSegments = JSON.stringify(currentSegments) === JSON.stringify(newSegments)
      }

      // Update travel data
      this.outboundTravel = travel
      this.updateOutboundTravelData()

      // Always preserve current tickets, equipment, seats and prices
      if (currentTickets.length) {
        this.outboundSelectedTickets = {
          tickets: currentTickets,
        }
        // Update passenger counts based on preserved tickets
        const passengers = currentTickets.map((ticket) => ({ type: ticket.passenger_type }))
        this.updatePassengerCounts('outbound', passengers, currentTickets)
      }

      if (currentEquipment.length) {
        this.outboundGroupedEquipment = currentEquipment
      }

      // Only preserve seats and prices if segments match for transfer journeys
      if (currentSeats.length && (!hasTransfers || sameSegments)) {
        this.outboundSelectedSeats = currentSeats
        this.outboundTotalSeatPrice = currentSeatPrice
      }

      // Save state to ensure data is persisted
      this.saveStateToLocalStorage()
    },

    setInboundTravel(travel: Travel | null) {
      const hasTransfers = Boolean(travel?.routes?.[0]?.legs?.length > 1)

      // Save current tickets, equipment, seats and prices before updating travel
      const currentTickets = this.inboundSelectedTickets?.tickets || []
      const currentEquipment = this.inboundGroupedEquipment || []
      const currentSeats = this.inboundSelectedSeats || []
      const currentSeatPrice = this.inboundTotalSeatPrice || 0

      // Check if the segments match for transfer journeys
      let sameSegments = true
      if (hasTransfers && travel?.routes?.[0]?.legs) {
        const currentSegments = this.inboundTravel?.routes?.[0]?.legs?.map((leg) => leg.id)
        const newSegments = travel.routes[0].legs.map((leg) => leg.id)
        sameSegments = JSON.stringify(currentSegments) === JSON.stringify(newSegments)
      }

      // Update travel data
      this.inboundTravel = travel
      this.updateInboundTravelData()

      // Always preserve current tickets, equipment, seats and prices
      if (currentTickets.length) {
        this.inboundSelectedTickets = {
          tickets: currentTickets,
        }
        // Update passenger counts based on preserved tickets
        const passengers = currentTickets.map((ticket) => ({ type: ticket.passenger_type }))
        this.updatePassengerCounts('inbound', passengers, currentTickets)
      }

      if (currentEquipment.length) {
        this.inboundGroupedEquipment = currentEquipment
      }

      // Only preserve seats and prices if segments match for transfer journeys
      if (currentSeats.length && (!hasTransfers || sameSegments)) {
        this.inboundSelectedSeats = currentSeats
        this.inboundTotalSeatPrice = currentSeatPrice
      }

      // Save state to ensure data is persisted
      this.saveStateToLocalStorage()
    },

    updatePassengerCounts(
      direction: 'outbound' | 'inbound',
      passengers: Passenger[],
      tickets: Ticket[],
    ) {
      const counts: Record<string, PassengerCount> = {}

      // Initialize counts for each passenger type
      passengers.forEach((passenger) => {
        counts[passenger.type] = {
          count: 0,
          price: null,
          name: '', // Will be updated from ticket
          product_family: '', // Will be updated from ticket
        }
      })

      // Count passengers and sum up prices
      tickets.forEach((ticket) => {
        const type = ticket.passenger_type

        if (!counts[type]) {
          counts[type] = {
            count: 0,
            price: null,
            name: '', // Will be updated from ticket
            product_family: ticket.product_family || '',
          }
        }

        counts[type].count++

        // Only update the price for the first ticket of each type
        if (counts[type].count === 1) {
          // Get the total price for all passengers of this type
          const sameTypeTickets = tickets.filter((t) => t.passenger_type === type)
          const totalPrice = sameTypeTickets.reduce((sum, t) => sum + (t.price?.amount || 0), 0)

          console.log('[updatePassengerCounts] Raw ticket price:', {
            type,
            totalPrice,
            count: counts[type].count,
            rawTicket: ticket,
          })

          const normalizedPrice = normalizePrice(
            {
              amount: totalPrice,
              currency_code: 'NOK',
            },
            'NOK',
            false,
          )

          console.log('[updatePassengerCounts] Normalized price:', normalizedPrice)
          counts[type].price = normalizedPrice
          counts[type].name = ticket.ticket_name || `${type} - ${ticket.product_family || ''}`
        }
      })

      // Update the store with the new counts
      if (direction === 'outbound') {
        this.outboundPassengerCounts = counts
      } else {
        this.inboundPassengerCounts = counts
      }

      this.saveStateToLocalStorage()
    },

    updateGroupedEquipment(direction: 'outbound' | 'inbound', equipment: Equipment[]) {
      // List of equipment types that are limited to one per passenger
      const onePerPassengerTypes = ['BK', 'TA', 'DG'] // Bike, Snowscooter, Pet/Dog

      equipment.forEach((item) => {
        const rawPrice = item.price?.amount || item.passenger_fares?.[0]?.price?.amount || 0
        console.log('[updateGroupedEquipment] Raw equipment price:', {
          name: item.name,
          rawPrice,
          price: item.price,
          passengerFarePrice: item.passenger_fares?.[0]?.price,
          productCode: item.product_code,
        })
      })

      // First group by UUID to avoid counting duplicates from transfer segments
      const uniqueEquipment = new Map<string, Equipment>()
      equipment.forEach((item) => {
        // For equipment types limited to one per passenger, use passenger ID in the key
        // For other equipment types, just use the UUID to deduplicate across segments
        const uniqueKey = onePerPassengerTypes.includes(item.product_code)
          ? `${item.passenger_fares?.[0]?.passenger_id}-${item.product_code}`
          : item.id || `${item.product_code}-${item.product_type}`

        if (!uniqueEquipment.has(uniqueKey)) {
          uniqueEquipment.set(uniqueKey, item)
        }
      })

      // Now group by product type for display
      const equipmentMap = new Map<string, Equipment>()
      Array.from(uniqueEquipment.values()).forEach((item) => {
        const key = `${item.product_code}-${item.product_type}`
        if (!equipmentMap.has(key)) {
          const rawPrice = item.price?.amount || item.passenger_fares?.[0]?.price?.amount || 0
          console.log('[updateGroupedEquipment] Processing price for:', {
            name: item.name,
            rawPrice,
            productCode: item.product_code,
          })

          equipmentMap.set(key, {
            ...item,
            product_type: item.product_type,
            product_code: item.product_code,
            product_family: item.product_family,
            name: item.name || item.product_type,
            count: 1,
            price: normalizePrice(
              {
                amount: rawPrice,
                currency_code: 'NOK',
              },
              'NOK',
              true,
            ), // Equipment prices come in as øre from the API
            passenger_fares: item.passenger_fares || [
              {
                passenger_id: '',
                price: item.price || {
                  amount: item.passenger_fares?.[0]?.price?.amount || 0,
                  currency_code: 'NOK',
                },
                inventory_class: item.passenger_fares?.[0]?.inventory_class,
                tariff_code: item.passenger_fares?.[0]?.tariff_code,
              },
            ],
          })
        } else {
          const existing = equipmentMap.get(key)!
          existing.count++
        }
      })

      const groupedEquipment = Array.from(equipmentMap.values())

      // If no equipment is provided or all equipment has count 0, clear the equipment for that direction
      if (equipment.length === 0 || equipment.every((e) => e.count === 0)) {
        if (direction === 'outbound') {
          this.outboundGroupedEquipment = []
          this.outboundTotalEquipmentPrice = { amount: 0, currency_code: 'NOK' }
        } else {
          this.inboundGroupedEquipment = []
          this.inboundTotalEquipmentPrice = { amount: 0, currency_code: 'NOK' }
        }
      } else {
        // Update with new equipment
        if (direction === 'outbound') {
          this.outboundGroupedEquipment = groupedEquipment
          this.outboundTotalEquipmentPrice = this.calculateTotalEquipmentPrice(groupedEquipment)
        } else {
          this.inboundGroupedEquipment = groupedEquipment
          this.inboundTotalEquipmentPrice = this.calculateTotalEquipmentPrice(groupedEquipment)
        }
      }

      // Save state to ensure equipment data is persisted
      this.saveStateToLocalStorage()
    },

    calculateTotalEquipmentPrice(equipment: Equipment[]): NormalizedPrice {
      const total = equipment.reduce((acc, item) => {
        return acc + (item.price?.amount || 0) * item.count
      }, 0)

      return {
        amount: total,
        currency: 'NOK',
        inOre: true, // Equipment prices are in øre
      }
    },

    updateSelectedSeats(
      direction: 'outbound' | 'inbound',
      seats: SelectedSeat[],
      totalPrice: NormalizedPrice,
    ) {
      // For transfer journeys, we need to preserve seats from other segments
      const currentSeats =
        direction === 'outbound' ? this.outboundSelectedSeats : this.inboundSelectedSeats

      // Create a map of existing seats by passenger ID to handle updates properly
      const seatsByPassenger = new Map<string, SelectedSeat[]>()
      currentSeats.forEach((seat) => {
        if (seat.passenger_id) {
          const passengerSeats = seatsByPassenger.get(seat.passenger_id) || []
          passengerSeats.push(seat)
          seatsByPassenger.set(seat.passenger_id, passengerSeats)
        }
      })

      // Process new seats, ensuring we update rather than add when a passenger already has a seat
      const updatedSeats = currentSeats.filter(
        (seat) =>
          // Keep seats from other segments
          !seats.some((newSeat) => newSeat.segmentId === seat.segmentId),
      )

      seats.forEach((newSeat) => {
        if (newSeat.passenger_id) {
          const existingSeats = seatsByPassenger.get(newSeat.passenger_id) || []
          const sameSegmentSeat = existingSeats.find((s) => s.segmentId === newSeat.segmentId)

          if (sameSegmentSeat) {
            // Update existing seat for this passenger in this segment
            const index = updatedSeats.findIndex(
              (s) =>
                s.segmentId === sameSegmentSeat.segmentId &&
                s.passenger_id === sameSegmentSeat.passenger_id,
            )
            if (index !== -1) {
              updatedSeats[index] = newSeat
            } else {
              updatedSeats.push(newSeat)
            }
          } else {
            // Add new seat for this passenger
            updatedSeats.push(newSeat)
          }
        }
      })

      // Calculate total price by summing prices from all segments
      const updatedTotalPrice = normalizeSeatPrice(
        updatedSeats.reduce((total, seat) => {
          const seatPrice =
            typeof seat.price === 'number'
              ? seat.price
              : typeof seat.price?.amount === 'number'
                ? seat.price.amount
                : 0
          return total + seatPrice
        }, 0),
      )

      if (direction === 'outbound') {
        this.outboundSelectedSeats = updatedSeats
        this.outboundTotalSeatPrice = updatedTotalPrice
      } else {
        this.inboundSelectedSeats = updatedSeats
        this.inboundTotalSeatPrice = updatedTotalPrice
      }

      // Save state to ensure seat prices are persisted
      this.saveStateToLocalStorage()
    },

    updateSelectedTickets(direction: 'outbound' | 'inbound', tickets: Ticket[]) {
      // Process the tickets first
      const processedTickets = tickets.map((ticket: Ticket) => {
        const passengerFare = ticket.passenger_fares?.[0]

        // First try to use fresh prices
        // Keep prices in kroner since that's how they come from the API
        const newPrice = normalizeTicketPrice(ticket.price || 0)
        const newTotalPrice = normalizeTicketPrice(
          ticket.total_price_to_be_paid || ticket.price || 0,
        )
        const newOriginalPrice = normalizeTicketPrice(ticket.original_price || ticket.price || 0)

        // Ensure we have the correct ticket name format
        const passengerTypeDisplay =
          ticket.passenger_type === 'ADT' ? 'Voksen' : ticket.passenger_type
        const ticketName =
          ticket.ticket_name || `${passengerTypeDisplay} - ${ticket.product_family || ''}`

        const apiTicket = {
          ...ticket,
          // Preserve the original passenger_fares structure for API compatibility
          passenger_fares: ticket.passenger_fares,
          // Keep prices in kroner since that's how they come from the API
          price: newPrice,
          total_price_to_be_paid: newTotalPrice,
          original_price: newOriginalPrice,
          ticket_name: ticketName,
        }

        // Convert to UI format
        return toUITicket(apiTicket)
      })

      // Update the tickets
      if (direction === 'outbound') {
        this.outboundSelectedTickets = {
          tickets: processedTickets,
        }
      } else {
        this.inboundSelectedTickets = {
          tickets: processedTickets,
        }
      }

      // Update total price - use raw amount since it's already normalized
      this.totalPriceToBePaid = normalizeTicketPrice(this.totalPrice.amount)
    },

    updateDiscountsAndTotal(bookingData: BookingData) {
      if (!bookingData) {
        return
      }

      // Update discounts
      this.discounts = bookingData.discounts || []

      // If total_price_to_be_paid is provided directly in booking data, use that
      if (bookingData.total_price_to_be_paid !== undefined) {
        this.totalPriceToBePaid = {
          amount: bookingData.total_price_to_be_paid,
          currency: 'NOK',
          inOre: false, // Since total_price_to_be_paid is in kroner
        }
      } else if (bookingData.total_price_unconverted) {
        // Fallback to total_price if total_price_to_be_paid is not available
        this.totalPriceToBePaid = {
          amount: bookingData.total_price_unconverted,
          currency: 'NOK',
          inOre: false, // total_price is also in kroner
        }
      }
    },

    updateOutboundTravelData() {
      if (!this.outboundTravel) return

      // Do not update tickets from travel data - they come from the booking store
      // This preserves prices during navigation

      // Try to get time from tickets first
      if (this.outboundSelectedTickets?.tickets?.length) {
        const firstTicket = this.outboundSelectedTickets.tickets[0]
        if (firstTicket?.departure_timestamp && firstTicket?.arrival_timestamp) {
          const departureTime = formatTime(firstTicket.departure_timestamp)
          const arrivalTime = formatTime(firstTicket.arrival_timestamp)

          const start = new Date(firstTicket.departure_timestamp)
          const end = new Date(firstTicket.arrival_timestamp)
          const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.outboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
          return
        }
      }

      // Fallback to travel data if no tickets or missing timestamps
      const route = this.outboundTravel.routes?.[0]
      if (route?.legs?.[0]) {
        const leg = route.legs[0]
        const departureTimestamp = leg.departure_station.departure_timestamp
        const arrivalTimestamp = leg.arrival_station.arrival_timestamp

        // Only proceed if we have valid timestamps
        if (departureTimestamp && arrivalTimestamp) {
          const departureTime = formatTime(departureTimestamp)
          const arrivalTime = formatTime(arrivalTimestamp)

          const start = new Date(departureTimestamp)
          const end = new Date(arrivalTimestamp)
          const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.outboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
        }
      }
    },

    updateInboundTravelData() {
      if (!this.inboundTravel) return

      // Do not update tickets from travel data - they come from the booking store
      // This preserves prices during navigation

      // Try to get time from tickets first
      if (this.inboundSelectedTickets?.tickets?.length) {
        const firstTicket = this.inboundSelectedTickets.tickets[0]
        if (firstTicket?.departure_timestamp && firstTicket?.arrival_timestamp) {
          const departureTime = formatTime(firstTicket.departure_timestamp)
          const arrivalTime = formatTime(firstTicket.arrival_timestamp)

          const start = new Date(firstTicket.departure_timestamp)
          const end = new Date(firstTicket.arrival_timestamp)
          const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.inboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
          return
        }
      }

      // Fallback to travel data if no tickets or missing timestamps
      const route = this.inboundTravel.routes?.[0]
      if (route?.legs?.[0]) {
        const leg = route.legs[0]
        const departureTimestamp = leg.departure_station.departure_timestamp
        const arrivalTimestamp = leg.arrival_station.arrival_timestamp

        // Only proceed if we have valid timestamps
        if (departureTimestamp && arrivalTimestamp) {
          const departureTime = formatTime(departureTimestamp)
          const arrivalTime = formatTime(arrivalTimestamp)

          const start = new Date(departureTimestamp)
          const end = new Date(arrivalTimestamp)
          const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.inboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
        }
      }
    },
  },
})
